import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface WaitingItem {
  id: string;
  person_type: string;
  person_name: string;
  related_email: string | null;
  related_phone: string | null;
  reason: string;
  category: string | null;
  internal_notes: string | null;
  tags: string[];
  related_instructor_id: string | null;
  related_proposal_id: string | null;
  related_packet_id: string | null;
  related_vendor_id: string | null;
  related_registration_id: string | null;
  related_interest_id: string | null;
  related_class_id: string | null;
  related_record_note: string | null;
  date_requested: string;
  expected_response_date: string | null;
  reminder_interval_days: number | null;
  next_reminder_at: string | null;
  escalation_date: string | null;
  completed_at: string | null;
  completion_method: string | null;
  priority: "low" | "normal" | "high" | "critical";
  status:
    | "waiting"
    | "reminder_scheduled"
    | "reminder_sent"
    | "response_received"
    | "completed"
    | "cancelled"
    | "escalated"
    | "overdue";
  created_at: string;
  updated_at: string;
}

export interface WaitingStats {
  totalActive: number;
  overdue: number;
  dueThisWeek: number;
  oldestDays: number;
  oldest: WaitingItem | null;
  avgWaitDays: number;
  remindersDueToday: number;
}

const DAY = 24 * 60 * 60 * 1000;
const ACTIVE_STATUSES = [
  "waiting",
  "reminder_scheduled",
  "reminder_sent",
  "response_received",
  "escalated",
  "overdue",
];

function computeStats(items: WaitingItem[]): WaitingStats {
  const now = Date.now();
  const active = items.filter((i) => ACTIVE_STATUSES.includes(i.status));
  const overdue = active.filter((i) => {
    if (i.status === "escalated" || i.status === "overdue") return true;
    return i.expected_response_date && new Date(i.expected_response_date).getTime() < now;
  });
  const in7 = now + 7 * DAY;
  const dueThisWeek = active.filter(
    (i) =>
      i.expected_response_date &&
      new Date(i.expected_response_date).getTime() >= now &&
      new Date(i.expected_response_date).getTime() <= in7,
  );
  const ages = active.map((i) => (now - new Date(i.date_requested).getTime()) / DAY);
  const oldestDays = ages.length ? Math.max(...ages) : 0;
  const oldest =
    active.length > 0
      ? active.reduce((a, b) =>
          new Date(a.date_requested).getTime() < new Date(b.date_requested).getTime() ? a : b,
        )
      : null;
  const avgWaitDays = ages.length ? ages.reduce((a, b) => a + b, 0) / ages.length : 0;
  const remindersDueToday = active.filter(
    (i) => i.next_reminder_at && new Date(i.next_reminder_at).getTime() <= now,
  ).length;
  return {
    totalActive: active.length,
    overdue: overdue.length,
    dueThisWeek: dueThisWeek.length,
    oldestDays: Math.round(oldestDays),
    oldest,
    avgWaitDays: Math.round(avgWaitDays * 10) / 10,
    remindersDueToday,
  };
}

export function useWaitingItems() {
  const [items, setItems] = useState<WaitingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("waiting_items" as any)
      .select("*")
      .order("date_requested", { ascending: true });
    if (error) setError(error.message);
    else setItems((data ?? []) as unknown as WaitingItem[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const channel = supabase
      .channel(`waiting-items-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "waiting_items" }, () =>
        refresh(),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [refresh]);

  return { items, loading, error, stats: computeStats(items), refresh };
}
